import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar, Footer } from '../components';
import api from '../utils/api';
import toast from 'react-hot-toast';

const OrderStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Menunggu' },
    processing: { color: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Diproses' },
    ready: { color: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20', label: 'Siap Kirim' },
    shipped: { color: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Dikirim' },
    delivered: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Tiba' },
    completed: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Selesai' },
    cancelled: { color: 'bg-rose-500/10 text-rose-500 border-rose-500/20', label: 'Dibatalkan' },
    refunded: { color: 'bg-rose-500/10 text-rose-500 border-rose-500/20', label: 'Dikembalikan' },
  };

  const config = statusConfig[status] || { color: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20', label: status };

  return (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${config.color}`}>
      {config.label}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  if (status === 'paid') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Lunas</span>;
  }
  if (status === 'refunded') {
    return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20">Refund</span>;
  }
  return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">Belum Bayar</span>;
};

const Orders = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Payment State
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchOrders(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, page]);

  // Load Midtrans Snap Script
  useEffect(() => {
    if (document.getElementById('midtrans-script')) return;
    const script = document.createElement('script');
    script.src = 'https://app.sandbox.midtrans.com/snap/snap.js';
    script.id = 'midtrans-script';
    document.body.appendChild(script);
    return () => {
      const el = document.getElementById('midtrans-script');
      if (el) el.remove();
    };
  }, []);

  const fetchOrders = async (pageNumber) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/orders?page=${pageNumber}`);
      setOrders(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Gagal mengambil data pesanan.');
    } finally {
      setLoading(false);
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(angka || 0);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const handleCompleteOrder = async (orderId) => {
    if (!window.confirm("Apakah Anda yakin telah menerima semua pesanan dengan baik dan ingin menyelesaikannya?")) return;
    
    try {
      await api.put(`/api/orders/${orderId}/status`, { status: 'completed' });
      toast.success('Pesanan berhasil diselesaikan!');
      fetchOrders(page);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Gagal menyelesaikan pesanan.');
    }
  };

  const handlePayWallet = async (orderId) => {
    if (!window.confirm("Bayar pesanan ini menggunakan Saldo Wallet Anda?")) return;
    setProcessingPayment(orderId);
    try {
      await api.post(`/api/payment/${orderId}/wallet`);
      toast.success("Pesanan berhasil dibayar menggunakan Saldo Wallet!");
      fetchOrders(page);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Pembayaran gagal.");
    } finally {
      setProcessingPayment(null);
    }
  };

  const handlePayMidtrans = async (orderId) => {
    setProcessingPayment(orderId);
    try {
      const snapRes = await api.post(`/api/payment/${orderId}/initiate`);
      const snapToken = snapRes.data.snap_token;
      
      if (window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: function (result) {
            toast.success("Pembayaran berhasil!");
            fetchOrders(page);
          },
          onPending: function (result) {
            toast.success("Menunggu pembayaran diselesaikan.");
            fetchOrders(page);
          },
          onError: function (result) {
            toast.error("Pembayaran gagal!");
          },
          onClose: function () {
            toast.error("Anda menutup jendela pembayaran.");
            fetchOrders(page);
          }
        });
      } else {
        toast.error("Gagal memuat gateway pembayaran.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Gagal menginisiasi pembayaran.");
    } finally {
      setProcessingPayment(null);
    }
  };

  const openReviewModal = (product) => {
    setSelectedProductForReview(product);
    setReviewForm({ rating: 5, comment: '' });
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedProductForReview(null);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForReview) return;
    
    setSubmittingReview(true);
    try {
      await api.post('/api/reviews', {
        product_id: selectedProductForReview.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      toast.success('Terima kasih atas ulasan Anda!');
      closeReviewModal();
      // Optional: We could mark this item as reviewed locally, but since API check happens...
      // Actually, relying on state or just letting them try again and getting 400 is simpler.
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Gagal mengirim ulasan.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-10 px-4 sm:px-6 lg:px-8 selection:bg-emerald-500/30">
        <div className="max-w-5xl mx-auto">
          
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Pesanan Saya</h1>
              <p className="mt-2 text-sm text-zinc-400">Lacak dan kelola riwayat transaksi Anda.</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="h-8 w-8 border-4 border-zinc-800 border-t-emerald-500 rounded-full animate-spin"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-12 text-center shadow-xl">
               <svg className="w-16 h-16 text-zinc-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
               </svg>
               <h3 className="text-xl font-bold text-white mb-2">Belum ada pesanan</h3>
               <p className="text-zinc-400 mb-6">Anda belum pernah melakukan transaksi apa pun. Yuk mulai belanja sekarang!</p>
               <Link 
                 to="/products"
                 className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 transition-all"
               >
                 Mulai Belanja
               </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-xl hover:border-zinc-700 transition duration-300 flex flex-col">
                  
                  {/* Order Header */}
                  <div className="bg-zinc-800/50 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono font-bold text-emerald-400">{order.order_code}</span>
                        <OrderStatusBadge status={order.status} />
                        <PaymentStatusBadge status={order.payment_status} />
                      </div>
                      <p className="text-xs text-zinc-400">{formatDate(order.created_at)}</p>
                    </div>
                    {/* Action conditional based on status */}
                    <div className="flex gap-2">
                       {order.payment_status !== 'paid' && order.status !== 'cancelled' && (
                          <>
                            <button
                              onClick={() => handlePayWallet(order.id)}
                              disabled={processingPayment === order.id}
                              className="flex-shrink-0 rounded-lg bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500 hover:text-white border border-indigo-500/20 px-4 py-2 text-sm font-bold transition-all disabled:opacity-50"
                            >
                              {processingPayment === order.id ? 'Memproses...' : 'Bayar (Wallet)'}
                            </button>
                            <button
                              onClick={() => handlePayMidtrans(order.id)}
                              disabled={processingPayment === order.id}
                              className="flex-shrink-0 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white border border-emerald-500/20 px-4 py-2 text-sm font-bold transition-all disabled:opacity-50"
                            >
                              {processingPayment === order.id ? 'Memproses...' : 'Bayar (Midtrans)'}
                            </button>
                          </>
                       )}
                       {(order.status === 'delivered' || order.status === 'ready' || order.status === 'shipped') && order.payment_status === 'paid' && (
                          <button
                            onClick={() => handleCompleteOrder(order.id)}
                            className="flex-shrink-0 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white border border-emerald-500/20 px-4 py-2 text-sm font-bold transition-all"
                          >
                            Terima Pesanan
                          </button>
                       )}
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="p-6 flex-grow flex flex-col gap-4">
                    {order.items?.map((item, idx) => (
                      <div key={item.id} className={`flex items-start gap-4 ${idx !== order.items.length - 1 ? 'pb-4 border-b border-zinc-800/50' : ''}`}>
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-zinc-800 border border-zinc-700">
                          <img
                            src={item.product?.image_url || '/placeholder.png'}
                            alt={item.product?.name || 'Produk'}
                            className="h-full w-full object-cover object-center"
                            onError={(e) => { e.target.src = '/placeholder.png'; }}
                          />
                        </div>
                        <div className="flex flex-1 flex-col justify-center">
                           <div className="flex justify-between items-start gap-4">
                             <div className="flex flex-col">
                               <Link to={item.product ? `/product/${item.product.slug}` : '#'} className="font-semibold text-white hover:text-emerald-400 transition-colors line-clamp-1">
                                 {item.product?.name || 'Produk Tidak Diketahui'}
                               </Link>
                               <span className="text-sm text-zinc-400 mt-1">{item.quantity} x {formatRupiah(item.price)}</span>
                             </div>
                             <p className="font-bold text-white text-right whitespace-nowrap">
                               {formatRupiah(item.quantity * item.price)}
                             </p>
                           </div>
                           
                           {order.status === 'completed' && item.product && (
                             <div className="mt-3 flex">
                               <button
                                 onClick={() => openReviewModal(item.product)}
                                 className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-xs font-bold text-amber-500 hover:bg-amber-500/10 hover:border-amber-500/30 transition-all"
                               >
                                 <svg className="w-4 h-4 fill-currentColor" viewBox="0 0 20 20">
                                   <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                 </svg>
                                 Beri Ulasan
                               </button>
                             </div>
                           )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Footer */}
                  <div className="bg-zinc-800/30 px-6 py-4 border-t border-zinc-800 flex flex-col sm:flex-row justify-between items-end sm:items-center gap-2">
                    <div className="text-sm text-zinc-400 flex flex-col gap-0.5 w-full sm:w-auto">
                      {order.discount_amount > 0 && (
                        <div className="flex justify-between sm:justify-start gap-4">
                          <span>Diskon:</span>
                          <span className="text-emerald-400 font-medium whitespace-nowrap">- {formatRupiah(order.discount_amount)}</span>
                        </div>
                      )}
                      {order.admin_fee > 0 && (
                        <div className="flex justify-between sm:justify-start gap-4">
                          <span>Biaya Admin:</span>
                          <span className="text-white whitespace-nowrap">{formatRupiah(order.admin_fee)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-4 items-center w-full sm:w-auto justify-between sm:justify-end mt-2 sm:mt-0">
                      <span className="text-sm font-medium text-zinc-400">Total Belanja:</span>
                      <span className="text-xl font-bold text-white">{formatRupiah(order.total_amount)}</span>
                    </div>
                  </div>

                </div>
              ))}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8 py-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm font-medium text-zinc-400 px-4">
                    Halaman <span className="text-white">{page}</span> dari {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedProductForReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-6 sm:p-8 shadow-2xl">
            <h2 className="text-xl font-extrabold text-white pb-4 border-b border-white/10">Beri Ulasan Produk</h2>
            
            <div className="flex items-center gap-4 mt-6 mb-6">
              <div className="h-16 w-16 overflow-hidden rounded-xl bg-zinc-800 shrink-0">
                <img src={selectedProductForReview.image_url || '/placeholder.png'} alt={selectedProductForReview.name} className="h-full w-full object-cover" />
              </div>
              <div>
                <p className="font-semibold text-white line-clamp-2">{selectedProductForReview.name}</p>
                <p className="text-sm text-zinc-400">{selectedProductForReview.store?.name}</p>
              </div>
            </div>

            <form onSubmit={handleReviewSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-zinc-300 mb-3 text-center">Kualitas Produk</label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                      className="focus:outline-none transition-transform hover:scale-110 active:scale-95"
                    >
                      <svg className={`w-10 h-10 ${star <= reviewForm.rating ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' : 'text-zinc-700'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.376 2.454a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.538 1.118l-3.376-2.454a1 1 0 00-1.176 0l-3.376 2.454c-.783.57-1.838-.196-1.538-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.286-3.967z" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Tulis Pengalamanmu</label>
                <textarea
                  rows="3"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Bagaimana kualitas produk ini? Apakah sesuai ekspektasi?"
                  className="w-full rounded-2xl bg-white/5 p-4 text-sm text-white ring-1 ring-white/10 focus:ring-emerald-500 outline-none resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeReviewModal}
                  className="flex-1 rounded-xl bg-white/5 py-3 text-sm font-bold text-zinc-300 hover:bg-white/10 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submittingReview}
                  className="flex-1 rounded-xl bg-emerald-500 py-3 text-sm font-bold text-white shadow-sm hover:bg-emerald-400 transition disabled:opacity-50"
                >
                  {submittingReview ? 'Mengirim...' : 'Kirim Ulasan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Orders;
